import { Area, BuildingAlternative } from '../stores/SubprojectStore';
import { EnergyKind } from './types';
import {
  createEmptyBuildingAlternative,
  createEmptyEnergyDemand,
} from './utils';

type ConvertArea = (area: Area) => Area[];

/**
 * Roll up a list of BuildingAlternatives (one per subproject) into a single
 * aggregate for the project overview tile.
 *
 * Pure and non-mutating: it never writes to the input alternatives (or their
 * nested `params` / cost / area objects), so it is safe to run repeatedly over
 * Zustand-store-owned data. Repeated categories are SUMMED across subprojects —
 * both `params` (concatenated) and every `costs` field (added), which is what
 * the project "Annual Costs" / Capital pies read per category.
 */
export function rollUpBuildingAlternatives(
  alts: BuildingAlternative[],
  convertArea: ConvertArea,
): BuildingAlternative {
  // Start from a full, well-typed empty alternative, then reset the fields we
  // accumulate (createEmpty* seeds a phantom `hvac` category + default areas).
  const acc = createEmptyBuildingAlternative();
  acc.technologyParams = {};
  acc.onSiteEnergy = { params: [] };
  acc.energyDemand = createEmptyEnergyDemand();
  acc.energyOnsiteSupply = {} as Record<EnergyKind, number>;
  acc.costs = {
    capitalCost: 0,
    maintenanceCost: 0,
    replacementCosts: 0,
    onsiteCosts: {},
  };
  acc.onSiteCosts = {
    capitalCost: 0,
    maintenanceCost: 0,
    majorMaintenanceCost: 0,
    regionalMultiplier: 0,
    technology: {},
  };
  let buildingParamsSet = false;

  const areaInUnit = (fromArea: Area, toArea: Area): number => {
    const found = convertArea(fromArea).find((ar) => ar.unit === toArea.unit);
    return found ? found.value : 0;
  };

  for (const raw of alts) {
    const b = raw ?? createEmptyBuildingAlternative();
    const bCosts = b.costs ?? {
      capitalCost: 0,
      maintenanceCost: 0,
      replacementCosts: 0,
    };

    // --- aggregate costs ---
    acc.costs.capitalCost += Number(bCosts.capitalCost || 0);
    // `?? 0`: a failed backend compute stores null figures.
    acc.costs.maintenanceCost += bCosts.maintenanceCost ?? 0;
    acc.costs.replacementCosts =
      (acc.costs.replacementCosts ?? 0) + (bCosts.replacementCosts ?? 0);
    for (const [cat, v] of Object.entries(bCosts.onsiteCosts ?? {})) {
      acc.costs.onsiteCosts![cat] = (acc.costs.onsiteCosts![cat] ?? 0) + v;
    }

    // --- per-category tech: SUM costs + concat params, always cloning ---
    for (const [key, incoming] of Object.entries(b.technologyParams ?? {})) {
      const existing = acc.technologyParams[key];
      if (existing) {
        acc.technologyParams[key] = {
          ...existing,
          params: [...existing.params, ...incoming.params],
          costs: {
            capitalCost:
              (existing.costs?.capitalCost ?? 0) +
              (incoming.costs?.capitalCost ?? 0),
            maintenanceCost:
              (existing.costs?.maintenanceCost ?? 0) +
              (incoming.costs?.maintenanceCost ?? 0),
            energyCost:
              (existing.costs?.energyCost ?? 0) +
              (incoming.costs?.energyCost ?? 0),
            replacementCosts:
              (existing.costs?.replacementCosts ?? 0) +
              (incoming.costs?.replacementCosts ?? 0),
          },
        };

        for (const [energyKey, v] of Object.entries(
          incoming.energyDemand ?? {},
        ) as [EnergyKind, number][]) {
          if (energyKey in acc.technologyParams[key]['energyDemand']) {
            acc.technologyParams[key]['energyDemand'][energyKey] =
              (acc.technologyParams[key]['energyDemand'][energyKey] ?? 0) + v;
          } else {
            acc.technologyParams[key]['energyDemand'][energyKey] = v;
          }
        }
      } else {
        // Deep-copy params + costs so later merges never touch store data.
        acc.technologyParams[key] = {
          ...incoming,
          params: [...incoming.params],
          costs: { ...incoming.costs },
          energyDemand: { ...incoming.energyDemand },
        };
      }
    }

    // --- on-site energy costs: aggregate totals + per-technology breakdown ---
    const bOnSite = b.onSiteCosts;
    if (bOnSite) {
      acc.onSiteCosts.capitalCost += bOnSite.capitalCost ?? 0;
      acc.onSiteCosts.maintenanceCost += bOnSite.maintenanceCost ?? 0;
      acc.onSiteCosts.majorMaintenanceCost =
        (acc.onSiteCosts.majorMaintenanceCost ?? 0) +
        (bOnSite.majorMaintenanceCost ?? 0);
      for (const [techId, entry] of Object.entries(bOnSite.technology ?? {})) {
        const existing = acc.onSiteCosts.technology[techId];
        acc.onSiteCosts.technology[techId] = existing
          ? {
              ...existing,
              capitalCost:
                (existing.capitalCost ?? 0) + Number(entry.capitalCost ?? 0),
              maintenanceCost:
                (existing.maintenanceCost ?? 0) +
                Number(entry.maintenanceCost ?? 0),
            }
          : { ...entry };
      }
    }

    // --- on-site energy params (clone, never push into store arrays) ---
    acc.onSiteEnergy = {
      ...acc.onSiteEnergy,
      params: [...acc.onSiteEnergy.params, ...(b.onSiteEnergy?.params ?? [])],
    };

    // --- energy demand ---
    for (const [key, v] of Object.entries(b.energyDemand ?? {}) as [
      EnergyKind,
      number,
    ][]) {
      acc.energyDemand[key] = (acc.energyDemand[key] ?? 0) + v;
    }

    // --- on-site energy supply ---
    for (const [key, v] of Object.entries(b.energyOnsiteSupply ?? {}) as [
      EnergyKind,
      number,
    ][]) {
      // We need to match up the subproject's supply record with the corresponding energyDemand record
      // to check and see if supply is greater than demand. If so, we actually want to set
      // the supply to be the same as demand otherwise supply calculations will break at the overview level.
      // It will end up looking like subproject energy sources are being made available to one another.

      const demand = b.energyDemand?.[key] ?? 0;
      acc.energyOnsiteSupply[key] =
        (acc.energyOnsiteSupply[key] ?? 0) + Math.min(v, demand);
    }

    // --- building params (first-seen clone, then accumulate into fresh objects) ---
    if (!buildingParamsSet) {
      acc.buildingParams = {
        ...b.buildingParams,
        conditionedSpace: { ...b.buildingParams.conditionedSpace },
        perviousArea: { ...b.buildingParams.perviousArea },
      };
      buildingParamsSet = true;
    } else {
      const bp = acc.buildingParams;
      acc.buildingParams = {
        ...bp,
        numberOfFloors: bp.numberOfFloors + b.buildingParams.numberOfFloors,
        numberOfOccupants:
          bp.numberOfOccupants + b.buildingParams.numberOfOccupants,
        conditionedSpace: {
          ...bp.conditionedSpace,
          value:
            bp.conditionedSpace.value +
            areaInUnit(b.buildingParams.conditionedSpace, bp.conditionedSpace),
        },
        perviousArea: {
          ...bp.perviousArea,
          value:
            bp.perviousArea.value +
            areaInUnit(b.buildingParams.perviousArea, bp.perviousArea),
        },
      };
    }
  }

  return acc;
}
